import * as React from 'react'
import { BarStack } from '@vx/shape'
import { GridRows } from '@vx/grid'
import { AxisBottom } from '@vx/axis'
import { scaleBand, scaleLinear, scaleOrdinal } from '@vx/scale'
import { withTooltip } from '@vx/tooltip'
import { LegendOrdinal } from '@vx/legend'
import { flow, map, uniq, keys, filter, flatten, max, sum } from 'lodash/fp'
import { GraphTooltip } from '../GraphTooltip'
import LinedTable from 'components/Tables/LinedTable'
import { localPoint } from '@vx/event'
import { omit, round } from 'lodash'
import * as colors from 'styles/colors-export.css'
import moment from 'moment'

const getKeys = flow<any, any, any, any, any>(
  map(keys),
  flatten,
  filter(i => i !== 'date'),
  uniq
)
export const getMax = (data, keys) =>
  flow<any[], any[], number>(
    map(item => sum(map(key => item[key], keys))),
    max
  )(data)

const x = d => d.date

export const GraphStackedBarComponent = ({
  data,
  width,
  height,
  margin = {
    top: 40,
    left: 0,
    bottom: 0,
    right: 0,
  },
  tooltipOpen,
  tooltipLeft,
  tooltipTop,
  tooltipData,
  hideTooltip,
  showTooltip,
  dateFormat,
}) => {
  if (width < 10) return null

  const keys = getKeys(data)
  const maxVal = getMax(data, keys)

  // bounds
  const xMax = width
  const yMax = height - margin.top - 20

  // scales
  const xScale = scaleBand({
    rangeRound: [0, xMax],
    domain: data.map(x),
    padding: 0.2,
    tickFormat: () => val => moment(val).format(dateFormat),
  })

  const yScale = scaleLinear({
    range: [yMax, 0],
    domain: [0, maxVal],
    clamp: true,
  })

  const yScaleDefaulted = (val = 0) => {
    const defaultedVal = isNaN(val) ? 0 : val
    return scaleLinear({
      rangeRound: [yMax, 0],
      nice: true,
      domain: [0, maxVal],
    })(defaultedVal)
  }

  const zScale = scaleOrdinal({
    domain: keys,
    range: [
      colors.graph1,
      colors.graph2,
      colors.graph3,
      colors.graph4,
      colors.graph5,
      colors.graph6,
      colors.graph7,
      colors.graph8,
    ],
  })

  return (
    <div style={{ position: 'relative' }}>
      <svg width={width} height={height}>
        <GridRows
          top={margin.top}
          scale={yScale}
          width={xMax}
          height={yMax}
          stroke="rgba(0,0,0,0.1)"
        />
        <BarStack
          top={margin.top}
          data={data}
          keys={keys}
          height={yMax}
          x={x}
          xScale={xScale}
          yScale={yScaleDefaulted}
          zScale={zScale}
          onMouseLeave={data => event => hideTooltip()}
          onMouseMove={data => event => {
            const coords = localPoint(event.target.ownerSVGElement, event)
            showTooltip({
              tooltipLeft: coords.x,
              tooltipTop: coords.y,
              tooltipData: omit(data.data, 'date'),
            })
          }}
        />
        <AxisBottom
          scale={xScale}
          top={yMax + margin.top}
          numTicks={5}
          stroke="rgba(0,0,0,0.1)"
          tickStroke="rgba(0,0,0,0.1)"
          tickLabelProps={(value, index) => ({
            fontSize: 10,
            textAnchor: 'middle',
          })}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          top: 0,
          display: 'flex',
          justifyContent: 'center',
          fontSize: '12px',
        }}
      >
        <LegendOrdinal scale={zScale} direction="row" labelMargin="0 15px 0 0" />
      </div>

      {tooltipOpen && (
        <GraphTooltip top={tooltipTop} left={tooltipLeft}>
          <LinedTable>
            {Object.keys(tooltipData).map(key => (
              <div key={key}>
                <div>{key}:</div>
                <div>{round(tooltipData[key], 2)}%</div>
              </div>
            ))}
          </LinedTable>
        </GraphTooltip>
      )}
    </div>
  )
}

export const GraphStackedBar = withTooltip(GraphStackedBarComponent)



// WEBPACK FOOTER //
// src/features/CallAnalytics/GraphStackedBar/GraphStackedBar.tsx